import { useEffect, useMemo, useRef, useState } from 'react';

export type BackgroundMode = 'none' | 'blur' | 'image';

export interface BackgroundChoice {
  mode: BackgroundMode;
  /** Object URL of the user's chosen image. Only meaningful when mode is 'image'. */
  imageUrl?: string;
}

export interface Background {
  /** The stream to preview *and* publish. Falls back to `local` when off. */
  stream: MediaStream | null;
  choice: BackgroundChoice;
  setChoice: (choice: BackgroundChoice) => void;
  /** True while the WASM downloads (~3 MB over the wire) and the segmenter warms up. */
  loading: boolean;
  error: string | null;
  supported: boolean;
}

/** Assets are served by Vortex itself — never a CDN. See scripts/vendor-mediapipe.mjs. */
const ASSET_PATHS = {
  tasksVisionFileSet: '/mediapipe/wasm',
  modelAssetPath: '/mediapipe/selfie_segmenter.tflite',
};

const BLUR_RADIUS = 12;

/**
 * Applies background blur or a replacement image to the local camera.
 *
 * The processor runs at the *local media* layer rather than on LiveKit's
 * published track, so the lobby preview, the self tile and what everyone else
 * receives are all the same frames. Applying it at publish time instead would
 * show you an unprocessed preview of a processed broadcast, which is exactly
 * the situation where someone leaves the house thinking their room is hidden.
 *
 * The library and its WASM load on demand — most calls never turn this on, and
 * it is 18 MB on disk (~3 MB compressed over the wire), cached for a year
 * afterwards.
 */
export function useBackground(local: MediaStream | null, active: boolean): Background {
  const [choice, setChoiceState] = useState<BackgroundChoice>({ mode: 'none' });
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState(true);

  // The live ProcessorWrapper, kept out of state so re-renders never restart it.
  const processorRef = useRef<{ destroy: () => Promise<void>; processedTrack?: MediaStreamTrack } | null>(null);
  const elementRef = useRef<HTMLVideoElement | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    const wanted = choice.mode;
    const sourceTrack = local?.getVideoTracks()[0] ?? null;
    const run = ++generation.current;

    const teardown = async () => {
      const current = processorRef.current;
      processorRef.current = null;
      if (current) await current.destroy().catch(() => {});
      elementRef.current?.remove();
      elementRef.current = null;
    };

    if (!active || wanted === 'none' || !sourceTrack) {
      void teardown().then(() => {
        if (generation.current === run) setStream(null);
      });
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const mod = await import('@livekit/track-processors');
        if (generation.current !== run) return;

        if (!mod.supportsBackgroundProcessors()) {
          setSupported(false);
          setError('This browser cannot do background effects.');
          setStream(null);
          return;
        }

        await teardown();
        if (generation.current !== run) return;

        const processor = mod.BackgroundProcessor(
          wanted === 'blur'
            ? { mode: 'background-blur', blurRadius: BLUR_RADIUS, assetPaths: ASSET_PATHS }
            : { mode: 'virtual-background', imagePath: choice.imageUrl!, assetPaths: ASSET_PATHS },
        );

        // The processor reads frames from a real <video>, and rejects anything
        // else outright. It is positioned off-screen rather than display:none
        // or detached — a hidden element can stop producing frames, which
        // shows up as a frozen picture rather than an error.
        const element = document.createElement('video');
        element.muted = true;
        element.autoplay = true;
        element.playsInline = true;
        element.srcObject = new MediaStream([sourceTrack]);
        element.style.cssText =
          'position:fixed;left:-9999px;top:0;width:2px;height:2px;opacity:0;pointer-events:none';
        document.body.appendChild(element);
        elementRef.current = element;
        await element.play().catch(() => {});

        await processor.init({ kind: 'video', track: sourceTrack, element } as never);
        if (generation.current !== run) {
          await processor.destroy().catch(() => {});
          return;
        }

        const processed = processor.processedTrack;
        if (!processed) throw new Error('the processor produced no video');

        processorRef.current = processor as unknown as typeof processorRef.current;

        // Audio is untouched — carry the original tracks across unchanged.
        const next = new MediaStream([processed, ...(local?.getAudioTracks() ?? [])]);
        setStream(next);
        setError(null);
      } catch (err) {
        if (generation.current !== run) return;
        setError(err instanceof Error ? err.message : 'Could not start the background effect');
        setStream(null);
      } finally {
        if (generation.current === run) setLoading(false);
      }
    })();

    return () => {
      // Only tear down on unmount; a choice change bumps the generation and the
      // new run disposes of the old processor itself.
    };
  }, [active, choice.mode, choice.imageUrl, local]);

  useEffect(() => {
    return () => {
      generation.current++;
      void processorRef.current?.destroy().catch(() => {});
      processorRef.current = null;
      elementRef.current?.remove();
      elementRef.current = null;
    };
  }, []);

  /* While an effect is wanted but the processor is still warming up — first
     join, a mode change, or the camera being switched back on — hold the raw
     camera back and carry audio alone. Letting it through "just until the blur
     starts" is precisely the moment someone's room goes out unblurred. */
  const exposed = useMemo(() => {
    if (stream) return stream;
    if (!local) return null;
    if (choice.mode === 'none' || !supported || error) return local;
    if (local.getVideoTracks().length === 0) return local;
    return new MediaStream(local.getAudioTracks());
  }, [stream, local, choice.mode, supported, error]);

  return {
    stream: exposed,
    choice,
    setChoice: setChoiceState,
    loading,
    error,
    supported,
  };
}
